import { KeyboardEvent, ReactNode, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, QuestionOutlineIcon } from "@chakra-ui/icons";

export type PassoComoUtilizar = {
  titulo: string;
  descricao: string;
};

export type ConceitoComoUtilizar = {
  termo: string;
  definicao: string;
};

// Um slide do modal: título (vai no cabeçalho, depois do nome da tela) e o
// conteúdo. "Como utilizar" (passos) e "Conceitos" são montados a partir das
// props; `slidesExtras` entra depois deles, pra tela acrescentar os seus.
export type SlideComoUtilizar = {
  titulo: string;
  conteudo: ReactNode;
};

type Props = {
  tituloTela: string;
  passos: PassoComoUtilizar[];
  conceitos?: ConceitoComoUtilizar[];
  slidesExtras?: SlideComoUtilizar[];
  // Nº de colunas em telas largas (padrão 3) — algumas telas têm poucos
  // passos/conceitos (ex.: Editar rotina, com 4 de cada) e ficam com uma
  // última linha capenga em 3 colunas; 2 evita isso.
  maxColunas?: number;
};

// Conceitos de escrituração/repasse — grounded em portais e aplicações/ADM GT/
// CLT - consignado/cobrança/Cobrança (ADM GT).md e produtos/CLT - consignado/
// DATAPREV/21-escrituracao-e-repasse.md (granatech-brain). Compartilhado por
// Vínculos Ativos e Vínculos Encerrados — as duas telas mostram esses status.
export const CONCEITOS_ESCRITURACAO_REPASSE: ConceitoComoUtilizar[] = [
  {
    termo: "Escriturado / Não escriturado",
    definicao: "O empregador registrou (ou não) o desconto da parcela no eSocial.",
  },
  {
    termo: "Repassado / Não repassado",
    definicao:
      "O valor já foi (ou não) transferido à instituição financeira, normalmente via Caixa, após a guia paga.",
  },
  {
    termo: "Pendente",
    definicao: "A competência ainda não teve nenhum evento.",
  },
  {
    termo: "Sucesso",
    definicao: "Escrituração e repasse confirmados, sem divergência.",
  },
  {
    termo: "Divergente",
    definicao:
      "Alguma checagem falhou: do lado da escrituração, o banco não confirmou os dados (trabalhador/contrato/vínculo/IF/valor batendo); do lado do repasse, o valor pago não bateu exatamente com o esperado.",
  },
];

// Mesmo botão "Como utilizar" já usado em Campanhas/Leads
// (granapix-frontend/ConsignadoAutoContratacaoPage): ghost com ícone de
// interrogação. O modal é um onboarding em slides — 1º "Como utilizar"
// (passos numerados), 2º "Conceitos", depois os `slidesExtras` da tela —
// com pontos de navegação, Anterior / Próximo e setas do teclado, em vez de
// uma página só com scroll.
const fontFamily = "Lexend Deca";

const cardProps = {
  bg: "white",
  border: "1px solid",
  borderColor: "gray.200",
  borderRadius: "lg",
  boxShadow: "sm",
  p: 4,
} as const;

// Em 2 colunas com quantidade ímpar, o último card ocupa a linha inteira.
const gridColumnUltimo = (maxColunas: number, total: number, index: number) =>
  maxColunas === 2 && total % 2 !== 0 && index === total - 1
    ? { base: "auto", md: "1 / -1" }
    : undefined;

function GridCards({ maxColunas, children }: { maxColunas: number; children: ReactNode }) {
  return (
    <SimpleGrid
      columns={{ base: 1, md: 2, lg: maxColunas }}
      spacing={4}
      sx={{ "@media print": { gridTemplateColumns: "repeat(2, 1fr)" } }}
    >
      {children}
    </SimpleGrid>
  );
}

function CardTexto({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <Box>
      <Text fontWeight="bold" color="#1E293B" fontSize={{ base: "sm", md: "md" }} mb={1} fontFamily={fontFamily}>
        {titulo}
      </Text>
      <Box fontSize="sm" color="#64748B" lineHeight="1.5" fontFamily={fontFamily}>
        {texto}
      </Box>
    </Box>
  );
}

export function SlidePassos({ passos, maxColunas = 3 }: { passos: PassoComoUtilizar[]; maxColunas?: number }) {
  return (
    <GridCards maxColunas={maxColunas}>
      {passos.map((passo, index) => (
        <Flex
          key={passo.titulo}
          {...cardProps}
          gap={3}
          align="flex-start"
          h="100%"
          gridColumn={gridColumnUltimo(maxColunas, passos.length, index)}
        >
          <Text
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="800"
            color="#1E293B"
            lineHeight="1"
            flexShrink={0}
            fontFamily={fontFamily}
          >
            {index + 1}.
          </Text>
          <CardTexto titulo={passo.titulo} texto={passo.descricao} />
        </Flex>
      ))}
    </GridCards>
  );
}

export function SlideConceitos({
  conceitos,
  maxColunas = 3,
}: {
  conceitos: ConceitoComoUtilizar[];
  maxColunas?: number;
}) {
  return (
    <GridCards maxColunas={maxColunas}>
      {conceitos.map((conceito, index) => (
        <Box key={conceito.termo} {...cardProps} gridColumn={gridColumnUltimo(maxColunas, conceitos.length, index)}>
          <CardTexto titulo={conceito.termo} texto={conceito.definicao} />
        </Box>
      ))}
    </GridCards>
  );
}

// Slides dos relatorios da secao Parcelas (botoes "Relatorio situacao das
// parcelas" e "Relatorio de quitacao" no cabecalho da tabela, ParcelasTable).
// Os textos espelham o que os proprios modais dizem (ParcelasRelatoriosModals /
// RelatorioQuitacaoModal) e o contrato das rotas TAR-041 e TAR-043/044.
// Compartilhado por Vinculos Ativos e Encerrados: as duas telas tem a tabela.
const PASSOS_RELATORIO_SITUACAO_PARCELAS: PassoComoUtilizar[] = [
  {
    titulo: "Abra a seção Parcelas",
    descricao:
      'Expanda o vínculo e, no cabeçalho da tabela de parcelas, clique em "Relatório situação das parcelas".',
  },
  {
    titulo: "Escolha o período (opcional)",
    descricao:
      "Início e fim filtram pelo vencimento das parcelas. Sem período, o relatório traz todas as parcelas da CCB.",
  },
  {
    titulo: "Exporte",
    descricao:
      "O PDF é gerado na hora com os dados da bancarizadora e abre em uma nova aba. Gerar de novo com o mesmo período substitui o arquivo anterior.",
  },
];

const PASSOS_RELATORIO_QUITACAO: PassoComoUtilizar[] = [
  {
    titulo: "Abra a seção Parcelas",
    descricao:
      'Expanda o vínculo e, no cabeçalho da tabela de parcelas, clique em "Relatório de quitação".',
  },
  {
    titulo: "Selecione as parcelas",
    descricao: "Marque as parcelas que entram na quitação, uma a uma ou todas de uma vez.",
  },
  {
    titulo: "Informe a data de cálculo",
    descricao: "O valor atualizado de cada parcela é calculado pela bancarizadora para essa data.",
  },
  {
    titulo: "Descapitalização",
    descricao:
      'Marque "Permitir descapitalização" para aplicar descapitalização e encargos no cálculo da bancarizadora.',
  },
  {
    titulo: "Exporte",
    descricao:
      "O PDF traz o total a quitar, calculado pela bancarizadora, e abre em uma nova aba. Se a bancarizadora não devolver o cálculo, a mensagem aparece no próprio modal.",
  },
];

// Slide da geracao de boletos (TAR-045/046) — espelha ParcelasTable,
// GerarBoletoModal e GerenciarBoletoModal.
const PASSOS_GERACAO_BOLETOS: PassoComoUtilizar[] = [
  {
    titulo: "Selecione as parcelas",
    descricao:
      "Na tabela de parcelas, marque as parcelas sem boleto ativo (um boleto cancelado não conta) e clique em \"Gerar boletos\".",
  },
  {
    titulo: "Vencimento e expiração",
    descricao:
      "Opcionais: sem vencimento a bancarizadora usa D+5; sem expiração, o boleto expira no vencimento.",
  },
  {
    titulo: "Liquidação e desconto",
    descricao:
      "Por padrão a cobrança é pela liquidação total. Desligue \"Liquidação total\" para informar o valor da liquidação, ou mantenha e informe um desconto opcional.",
  },
  {
    titulo: "Forma de cobrança e descrição",
    descricao:
      "Boleto e PIX podem ser marcados juntos ou só um deles (ao menos um). A descrição é opcional e acompanha a cobrança.",
  },
  {
    titulo: "Parcela escriturada",
    descricao:
      "Se a bancarizadora recusar por escrituração, a mensagem fica no modal. Marque \"Ignorar escrituração\", informe o motivo e reenvie — fica registrado com seu usuário.",
  },
  {
    titulo: "Acompanhe e gerencie",
    descricao:
      "Após gerar, a parcela mostra \"Boleto em processamento\" até o boleto entrar na tabela. Clicando no boleto você baixa o PDF ou cancela; boleto pago ou cancelado não tem ações.",
  },
];

export const SLIDE_GERACAO_BOLETOS: SlideComoUtilizar = {
  titulo: "Geração de boletos",
  conteudo: <SlidePassos passos={PASSOS_GERACAO_BOLETOS} />,
};

export const SLIDES_RELATORIOS_PARCELAS: SlideComoUtilizar[] = [
  {
    titulo: "Relatório situação das parcelas",
    conteudo: <SlidePassos passos={PASSOS_RELATORIO_SITUACAO_PARCELAS} />,
  },
  {
    titulo: "Relatório de quitação",
    conteudo: <SlidePassos passos={PASSOS_RELATORIO_QUITACAO} />,
  },
];

export default function ComoUtilizarModal({
  tituloTela,
  passos,
  conceitos,
  slidesExtras = [],
  maxColunas = 3,
}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [atual, setAtual] = useState(0);

  const slides: SlideComoUtilizar[] = [
    { titulo: "Como utilizar", conteudo: <SlidePassos passos={passos} maxColunas={maxColunas} /> },
    ...(conceitos && conceitos.length > 0
      ? [{ titulo: "Conceitos", conteudo: <SlideConceitos conceitos={conceitos} maxColunas={maxColunas} /> }]
      : []),
    ...slidesExtras,
  ];
  const ultimo = slides.length - 1;
  const slide = slides[Math.min(atual, ultimo)];

  const abrir = () => {
    setAtual(0);
    onOpen();
  };
  const anterior = () => setAtual((i) => Math.max(0, i - 1));
  const proximo = () => setAtual((i) => Math.min(ultimo, i + 1));

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") anterior();
    if (e.key === "ArrowRight") proximo();
  };

  return (
    <>
      <Button
        leftIcon={<QuestionOutlineIcon />}
        variant="outline"
        borderColor="whiteAlpha.600"
        color="white"
        _hover={{ bg: "whiteAlpha.200" }}
        fontFamily={fontFamily}
        fontWeight="normal"
        size="sm"
        onClick={abrir}
      >
        Como utilizar
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "5xl" }} scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent onKeyDown={handleKeyDown}>
          <ModalHeader fontFamily={fontFamily}>
            {tituloTela} - {slide.titulo}
          </ModalHeader>
          <ModalCloseButton />
          {/* minH segura a altura entre slides; o flex + my="auto" centraliza
              o conteúdo curto no espaço que sobra (e vira 0 quando rola). */}
          <ModalBody pb={2} minH={{ md: "320px" }} display="flex" flexDirection="column">
            <Box my="auto">{slide.conteudo}</Box>
          </ModalBody>
          <ModalFooter justifyContent="space-between" borderTopWidth="1px" borderTopColor="gray.200" pt={3}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ChevronLeftIcon />}
              onClick={anterior}
              isDisabled={atual === 0}
              fontFamily={fontFamily}
              fontWeight="normal"
            >
              Anterior
            </Button>

            <HStack spacing={2}>
              {slides.map((s, index) => (
                <Box
                  key={s.titulo}
                  as="button"
                  type="button"
                  aria-label={s.titulo}
                  aria-current={index === atual ? "step" : undefined}
                  w={index === atual ? 6 : 2}
                  h={2}
                  borderRadius="full"
                  bg={index === atual ? "primary" : "gray.300"}
                  transition="all 0.2s"
                  onClick={() => setAtual(index)}
                />
              ))}
            </HStack>

            {atual < ultimo ? (
              <Button
                colorScheme="primary"
                size="sm"
                rightIcon={<ChevronRightIcon />}
                onClick={proximo}
                fontFamily={fontFamily}
                fontWeight="normal"
              >
                Próximo
              </Button>
            ) : (
              <Button colorScheme="primary" size="sm" onClick={onClose} fontFamily={fontFamily} fontWeight="normal">
                Concluir
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
